/** leads/CandidatePromoteModal.tsx — 후보 → 리드 승격 (단계형 폼: 사업자번호 → 증자 내역 → 보완) */
import React, { useState } from 'react';
import { promoteCandidate } from './api';
import type { Candidate } from './types';
import { eligibleYears } from './calc';

interface Props { candidate: Candidate; onClose: () => void; onDone: () => void; onToast: (m: string, t?: string) => void }

const fmtBiz = (d: string) => (d.length > 5 ? d.slice(0, 3) + '-' + d.slice(3, 5) + '-' + d.slice(5) : d.length > 3 ? d.slice(0, 3) + '-' + d.slice(3) : d);
const fmtAmt = (d: string) => (d ? Number(d).toLocaleString() : '');

export default function CandidatePromoteModal({ candidate, onClose, onDone, onToast }: Props) {
  const [bizNo, setBizNo] = useState('');
  const [invests, setInvests] = useState<{ year: string; amount: string }[]>([{ year: '', amount: '' }]);
  const [ceoName, setCeoName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  const periods = candidate.periods && candidate.periods.length
    ? candidate.periods
    : [{ id: candidate.id, venture_from: candidate.venture_from, venture_to: candidate.venture_to, renew_type: candidate.renew_type }];
  const iy = parseInt(invests[0].year, 10);
  const yr = (d: string | null) => (d ? new Date(String(d)).getFullYear() : 0);
  const matched = (iy ? periods.find((p2) => yr(p2.venture_from) <= iy + 2 && !!p2.venture_to && iy <= yr(p2.venture_to)) : null) || periods[0]; // §16: 투자 후 2년 내 겹치면 성립
  const preview = iy ? eligibleYears(iy, String(matched.venture_from).slice(0, 10), matched.venture_to ? String(matched.venture_to).slice(0, 10) : null) : [];

  const submit = async () => {
    const digits = bizNo.replace(/[^0-9]/g, '');
    if (digits.length !== 10) { onToast('사업자번호는 10자리여야 합니다', 'error'); return; }
    const rows = invests.map((v) => ({ year: parseInt(v.year, 10), amount: v.amount ? Number(v.amount.replace(/[^0-9]/g, '')) : null })).filter((v) => v.year > 0);
    if (rows.length === 0) { onToast('증자연도를 1건 이상 입력하세요', 'error'); return; }
    if (new Set(rows.map((v) => v.year)).size !== rows.length) { onToast('같은 증자연도가 중복 입력됐습니다', 'error'); return; }
    if (preview.length === 0 && !window.confirm('첫 증자연도의 가능연도가 없습니다(시효 도과 또는 벤처기간 불일치).\n그래도 리드로 등록할까요?')) return;
    setBusy(true);
    try {
      const r = await promoteCandidate(candidate.id, { biz_no: digits, invests: rows, ceo_name: ceoName.trim(), phone: phone.trim() });
      onToast('리드 승격 완료 — 신규 ' + r.created + '건' + (r.existed > 0 ? ' · 기존 연결 ' + r.existed + '건' : '') + ' (경정청구 대상에서 배정하세요)');
      onDone();
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };

  return (
    <div className="ld-modal-bg ld-root" onClick={onClose}>
      <div className="ld-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ld-modal-h">
          <div><span>리드 승격</span><span className="pm-sub">{candidate.corp_name} · {candidate.confirm_type}</span></div>
          <button className="ld-btn" onClick={onClose}>닫기</button>
        </div>
        <div className="ld-modal-b">
          {/* 회사 요약 — 정의 그리드 */}
          <div className="dr2-grid">
            <div className="dr2-cell"><span className="k">지역</span><span className="v">{candidate.region} {candidate.sigungu || ''}</span></div>
            <div className="dr2-cell"><span className="k">대표자 (명단 익명)</span><span className="v">{candidate.ceo_masked || '—'}</span></div>
            <div className="dr2-cell wide"><span className="k">주생산품 · 업종</span><span className="v" style={{ fontWeight: 500 }}>{candidate.product || '—'}{candidate.industry ? ' · ' + candidate.industry : ''}</span></div>
            <div className="dr2-cell wide hi"><span className="k">벤처 유효기간 — 증자연도에 맞는 기간이 자동 적용</span>
              <span className="v" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{periods.map((p2) => (
                <span key={p2.id} className={'pm-period' + (p2.id === matched.id && iy > 0 ? ' on' : '')}>
                  {String(p2.venture_from).slice(0, 10)} ~ {p2.venture_to ? String(p2.venture_to).slice(0, 10) : '-'}{p2.id === matched.id && iy > 0 ? ' ✓ 적용' : ''}
                </span>
              ))}</span>
            </div>
          </div>

          {/* STEP 1 */}
          <div className="pm-step"><span className="n">1</span>사업자등록번호 <b className="req">*</b><span className="pm-hint">크레탑에서 확인</span></div>
          <input className="pm-bizno" autoFocus value={fmtBiz(bizNo)} placeholder="000-00-00000" inputMode="numeric"
            onChange={(e) => setBizNo(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))} />

          {/* STEP 2 */}
          <div className="pm-step"><span className="n">2</span>증자 내역 <b className="req">*</b><span className="pm-hint">여러 번이면 전부 · 설립연도 출자(초기 자본금)도 대상 — 금액은 선택</span></div>
          <div className="pm-cols"><span>증자연도</span><span>증자금액(원)</span><span /></div>
          {invests.map((v, i) => (
            <div key={i} className="pm-row">
              <input value={v.year} placeholder={String(2021 + i)} inputMode="numeric" maxLength={4}
                onChange={(e) => { const t = e.target.value.replace(/[^0-9]/g, ''); setInvests((a) => a.map((x, j) => (j === i ? { ...x, year: t } : x))); }} />
              <input value={fmtAmt(v.amount)} placeholder="300,000,000"
                onChange={(e) => { const t = e.target.value.replace(/[^0-9]/g, ''); setInvests((a) => a.map((x, j) => (j === i ? { ...x, amount: t } : x))); }} />
              {invests.length > 1
                ? <button className="pm-x" title="이 행 삭제" onClick={() => setInvests((a) => a.filter((_, j) => j !== i))}>×</button>
                : <span />}
            </div>
          ))}
          {invests.length < 6 && <button className="ld-btn pm-add" onClick={() => setInvests((a) => [...a, { year: '', amount: '' }])}>+ 증자 추가</button>}

          {/* STEP 3 */}
          <div className="pm-step"><span className="n">3</span>보완 정보<span className="pm-hint">선택 — 크레탑에서 실명 확인되면 입력</span></div>
          <div className="pm-row two">
            <input value={ceoName} onChange={(e) => setCeoName(e.target.value)} placeholder="대표자 실명" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="연락처" />
          </div>

          {/* 미리보기 */}
          <div className={'pm-preview' + (iy > 0 && preview.length === 0 ? ' bad' : '')}>
            <b>경정청구 가능연도</b>
            {!iy && <span className="pm-hint">증자연도를 입력하면 자동 계산됩니다</span>}
            {iy > 0 && preview.length === 0 && <span className="ld-year expired">가능연도 없음 — 시효 도과 또는 벤처기간 불일치</span>}
            {preview.map((y) => <span key={y.year} className={'ld-year' + (y.daysLeft <= 180 ? ' urgent' : '')}>귀속 {y.year} · 마감 {y.deadline} (D-{y.daysLeft})</span>)}
          </div>
        </div>
        <div className="ld-modal-f">
          <button className="ld-btn" onClick={onClose}>취소</button>
          <button className="ld-btn pri" disabled={busy} onClick={submit}>{busy ? '처리 중…' : '리드로 승격 →'}</button>
        </div>
      </div>
    </div>
  );
}
